import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AdminAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing | error
  const [error, setError] = useState('');

  useEffect(() => {
    // Guard against StrictMode double-effect
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const complete = async () => {
      // Parse session_id from URL fragment
      const hash = location.hash || window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;

      const tempToken = sessionStorage.getItem('admin_2fa_temp_token');
      const adminId = sessionStorage.getItem('admin_2fa_admin_id');
      const deviceId = sessionStorage.getItem('admin_2fa_device_id');

      if (!sessionId) {
        setStatus('error');
        setError('Google verification did not return a session. Please try again.');
        return;
      }
      if (!tempToken || !adminId || !deviceId) {
        setStatus('error');
        setError('Verification session expired. Please login again.');
        return;
      }

      // Clean URL fragment before further navigation
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (e) { /* noop */ }

      try {
        // Best-effort device name
        const ua = navigator.userAgent || '';
        let browser = 'Browser';
        let osName = 'Device';
        [['Chrome', /Chrome/i], ['Firefox', /Firefox/i], ['Edge', /Edg/i], ['Safari', /Safari/i]].some(([n, r]) => {
          if (r.test(ua)) { browser = n; return true; } return false;
        });
        [['Windows', /Windows/i], ['Mac', /Mac OS/i], ['iPhone', /iPhone/i], ['iPad', /iPad/i], ['Android', /Android/i], ['Linux', /Linux/i]].some(([n, r]) => {
          if (r.test(ua)) { osName = n; return true; } return false;
        });
        const deviceName = `${browser} on ${osName}`;

        const resp = await axios.post(`${API}/admin/verify-google`, {
          session_id: sessionId,
          temp_token: tempToken,
          device_id: deviceId,
          device_name: deviceName,
        });

        const userData = resp.data;

        // Clear pending 2FA state
        sessionStorage.removeItem('admin_2fa_temp_token');
        sessionStorage.removeItem('admin_2fa_admin_id');
        sessionStorage.removeItem('admin_2fa_device_id');

        login(userData);

        // Track activity for login
        try {
          await axios.post(`${API}/admin/activity/track`, {
            user_id: userData.id,
            action: 'login',
          });
        } catch (activityErr) {
          console.warn('Activity tracking failed:', activityErr);
        }

        navigate('/admin-dashboard', { replace: true });
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Google verification failed. Please try again.');
      }
    };

    complete();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'processing' ? (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
              <ShieldCheck size={40} weight="fill" className="text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="admin-auth-processing">
              Verifying with Google...
            </h2>
            <p className="text-sm text-gray-500">
              Please wait while we confirm your identity.
            </p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
              <WarningCircle size={40} weight="fill" className="text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="admin-auth-error">
              Verification Failed
            </h2>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin-login', { replace: true })}
              className="w-full py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
              data-testid="admin-auth-back-btn"
            >
              Back to Admin Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAuthCallback;
